// "Complete this lead" (client, 2026-10-28): an open record handled by the
// person looking at it must carry a product, the details of the person they
// spoke to, and a next follow-up before they work on it. IndiaMART leads
// arrive with none of these. The dialog cannot be dismissed — only completed,
// or left by going back to the pipeline — and it reuses the record page's own
// product editor and the follow-up API, so what it saves is exactly what the
// page would have saved.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { ROUTES } from '@/routes/paths';
import { useCreateTimelineRow, useUpdateTimelineRow } from '@/features/activity/hooks/useFollowups';
import { ProductLinesEditor } from './ProductLinesEditor';
import type { LeadWorkGap } from '../lead-work';
import type { PipelineLine, PipelineRecord } from '../types';

/** Tomorrow 11:00, as a datetime-local value. */
function defaultNext() {
  const d = new Date(Date.now() + 24 * 3600 * 1000);
  d.setHours(11, 0, 0, 0);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const CONTACT_METHODS = [
  { value: 'call', label: 'Call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'visit', label: 'Visit' },
  { value: 'other', label: 'Other' },
];

/** 10 digits, optionally with a country code — what a reachable number needs. */
const validPhone = (v: string) => {
  const digits = v.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 13;
};

interface Props {
  record: PipelineRecord;
  parentType: 'lead' | 'deal';
  gaps: LeadWorkGap[];
  /** The open follow-up to attach contact details to, when one already exists. */
  openFollowupId?: string | null;
  onLinesChange: (lines: PipelineLine[]) => void;
  /** Fill the record's own contact name / phone when they are empty. */
  onContactSaved: (contact: { name: string; phone: string }) => void;
  /**
   * true (default): the assigned salesperson — cannot be closed. false: an
   * admin / manager completing it on their behalf — closable via onClose.
   */
  required?: boolean;
  onClose?: () => void;
}

const input = 'rounded-lg border border-outline bg-surface px-2.5 py-1.5';

export function CompleteLeadDialog({ record, parentType, gaps, openFollowupId, onLinesChange, onContactSaved, required = true, onClose }: Props) {
  const navigate = useNavigate();
  const createFollowup = useCreateTimelineRow('followups');
  const updateFollowup = useUpdateTimelineRow('followups');
  const needsProduct = gaps.includes('product');
  const needsContact = gaps.includes('contact');
  const needsFollowup = gaps.includes('followup');

  // The enquiry's own name and number are the usual starting point.
  const [personName, setPersonName] = useState(record.contact_name || '');
  const [personPhone, setPersonPhone] = useState(record.phone || '');
  const [personDesignation, setPersonDesignation] = useState('');
  const [personEmail, setPersonEmail] = useState(record.email || '');
  const [when, setWhen] = useState(defaultNext);
  const [method, setMethod] = useState('call');
  const [note, setNote] = useState('');
  // "No next follow-up needed" (client, 2026-10-30) — the call is still
  // recorded, closed, with why nothing more is scheduled.
  const [noNext, setNoNext] = useState(false);
  const [noNextReason, setNoNextReason] = useState('');
  const [error, setError] = useState('');
  const busy = createFollowup.isPending || updateFollowup.isPending;
  const title = record.business_name || record.contact_name || record.title || 'this lead';

  const contactFields = () => ({
    contactPersonName: personName.trim(),
    contactPersonPhone: personPhone.trim(),
    contactPersonDesignation: personDesignation.trim(),
    contactPersonEmail: personEmail.trim(),
  });

  const save = () => {
    setError('');
    if (needsContact) {
      if (!personName.trim()) { setError('Write the name of the person you spoke to.'); return; }
      if (!validPhone(personPhone)) { setError('Write a 10-digit mobile number for the person you spoke to.'); return; }
    }
    if (needsFollowup) {
      if (noNext) {
        if (!note.trim()) { setError('Write what you discussed.'); return; }
        if (noNextReason.trim().length < 10) { setError('Say why no next follow-up is needed (at least 10 characters).'); return; }
      } else {
        if (!when) { setError('Pick the date of the next follow-up.'); return; }
        if (new Date(when).getTime() < Date.now() - 60_000) { setError('The next follow-up must be in the future.'); return; }
        if (!note.trim()) { setError('Write what you discussed, or what the next call is for.'); return; }
      }
    }
    const done = () => {
      if (needsContact) onContactSaved({ name: personName.trim(), phone: personPhone.trim() });
      toast.success('Saved.');
    };
    const failed = () => setError('Could not save. Try again.');
    if (needsFollowup) {
      createFollowup.mutate(
        {
          parentType,
          parentId: record.id,
          followupDate: noNext ? new Date().toISOString() : new Date(when).toISOString(),
          followupNote: note.trim(),
          contactMethod: method,
          status: 'open',
          ...(noNext ? { noNext: true, noNextReason: noNextReason.trim() } : {}),
          ...(needsContact ? contactFields() : {}),
        },
        { onSuccess: done, onError: failed },
      );
    } else if (needsContact && openFollowupId) {
      // parentType / parentId are ignored by the server on an update; they are
      // here so the hook refreshes this record's follow-up list.
      updateFollowup.mutate(
        { id: openFollowupId, patch: { ...contactFields(), parentType, parentId: record.id } },
        { onSuccess: done, onError: failed },
      );
    }
  };

  const step = (n: number, open: boolean) => (open
    ? <span className="flex h-5 w-5 items-center justify-center rounded-full bg-danger/15 text-data-xs font-bold text-danger-text">{n}</span>
    : <CheckCircle2 className="h-5 w-5 text-success-text" />);

  return (
    <Modal
      open
      onClose={required ? () => { /* required — completed, or left via "Back to pipeline" */ } : (onClose ?? (() => {}))}
      closeOnBackdrop={false}
      dismissible={!required}
      size="xl"
      title={`Complete this lead — ${title}`}
      footer={(
        <div className="flex w-full items-center justify-between gap-3">
          <p className="text-body-sm text-on-surface-variant">
            Every open lead needs a product, the person you spoke to, and a next follow-up.
          </p>
          {required
            ? <Button variant="secondary" onClick={() => navigate(ROUTES.pipeline)}>Back to pipeline</Button>
            : <Button variant="secondary" onClick={onClose}>Close</Button>}
        </div>
      )}
    >
      <div className="space-y-5">
        <section className="rounded-lg border border-outline p-3">
          <h3 className="mb-2 flex items-center gap-2 text-body-md font-semibold">{step(1, needsProduct)} Product the customer asked about</h3>
          {/* Stays open after the first product — a customer often asks for
              several (client, 2026-10-28). A sent quotation fixes the products. */}
          {record.hasIssuedQuotation ? (
            <p className="text-body-sm text-success-text">Products are on the quotation already sent.</p>
          ) : (
            <>
              <ProductLinesEditor lines={record.lines ?? []} onChange={onLinesChange} currency={record.currency || 'INR'} />
              {!needsProduct && (
                <p className="mt-2 text-body-sm text-on-surface-variant">
                  {(record.lines ?? []).length} product{(record.lines ?? []).length === 1 ? '' : 's'} added — add more if the customer asked for several.
                </p>
              )}
            </>
          )}
        </section>

        <section className="rounded-lg border border-outline p-3">
          <h3 className="mb-2 flex items-center gap-2 text-body-md font-semibold">{step(2, needsContact)} Person you spoke to</h3>
          {needsContact ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-body-sm">
                <span className="font-medium">Name *</span>
                <input id="lead-person-name" value={personName} onChange={(e) => setPersonName(e.target.value)} placeholder="e.g. Ramesh Patil" className={input} />
              </label>
              <label className="flex flex-col gap-1 text-body-sm">
                <span className="font-medium">Mobile *</span>
                <input id="lead-person-phone" value={personPhone} onChange={(e) => setPersonPhone(e.target.value)} inputMode="tel" placeholder="10-digit mobile" className={input} />
              </label>
              <label className="flex flex-col gap-1 text-body-sm">
                <span className="font-medium">Designation</span>
                <input id="lead-person-designation" value={personDesignation} onChange={(e) => setPersonDesignation(e.target.value)} placeholder="e.g. Purchase Manager" className={input} />
              </label>
              <label className="flex flex-col gap-1 text-body-sm">
                <span className="font-medium">Email</span>
                <input id="lead-person-email" value={personEmail} onChange={(e) => setPersonEmail(e.target.value)} type="email" className={input} />
              </label>
            </div>
          ) : (
            <p className="text-body-sm text-success-text">Contact details recorded.</p>
          )}
        </section>

        <section className="rounded-lg border border-outline p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-body-md font-semibold">{step(3, needsFollowup)} Next follow-up</h3>
            {needsFollowup && (
              <label className="flex cursor-pointer items-center gap-2 text-body-sm text-on-surface-variant">
                <input id="lead-no-next" type="checkbox" checked={noNext} onChange={(e) => { setNoNext(e.target.checked); setError(''); }}
                  className="h-4 w-4 rounded border-input accent-primary" />
                No next follow-up needed
              </label>
            )}
          </div>
          {needsFollowup ? (
            <div className={noNext ? 'grid gap-3 sm:grid-cols-[10rem_1fr]' : 'grid gap-3 sm:grid-cols-[14rem_10rem_1fr]'}>
              {!noNext && (
                <label className="flex flex-col gap-1 text-body-sm">
                  <span className="font-medium">When *</span>
                  <input id="lead-next-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={input} />
                </label>
              )}
              <label className="flex flex-col gap-1 text-body-sm">
                <span className="font-medium">How</span>
                <select id="lead-next-method" value={method} onChange={(e) => setMethod(e.target.value)} className={input}>
                  {CONTACT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-body-sm">
                <span className="font-medium">Note *</span>
                <input id="lead-next-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Called, wants rate for 26 L — send quotation" className={input} />
              </label>
              {noNext && (
                <label className="flex flex-col gap-1 text-body-sm sm:col-span-2">
                  <span className="font-medium">Why is no next follow-up needed? *</span>
                  <textarea id="lead-no-next-reason" rows={2} value={noNextReason} onChange={(e) => { setNoNextReason(e.target.value); setError(''); }}
                    placeholder="e.g. Customer will call us when the plant restarts in March" className={input} />
                </label>
              )}
            </div>
          ) : (
            <p className="text-body-sm text-success-text">Next follow-up set.</p>
          )}
        </section>

        {(needsContact || needsFollowup) && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-body-sm text-danger-text">{error}</span>
            <Button onClick={save} disabled={busy}>
              {needsFollowup ? (needsContact ? 'Save contact & follow-up' : 'Save follow-up') : 'Save contact details'}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
